import { WC_LIMITS, type WcFragment, type WcFeedback } from '../protocol/WebCodecsTypes';
import type { WcCompleteFrame } from '../client/WebCodecsClientTypes';

type Assembly = {
  header: WcFragment; bytes: Uint8Array; parts: Map<number, { offset: number; size: number }>;
  filled: number; firstUs: number; lastUs: number; deadlineUs: number;
  /** When this frame's confirmed loss first went on the wire; the next fragment to land measures the repair's round trip. */
  reportedUs: number | null;
};
/** A frame below the highest seen of which nothing has landed: its first fragment is asked for by id. */
type Gap = { seenUs: number; deadlineUs: number; reportedUs: number | null };
/**
 * A key may be paced in over most of a second on a slow path; a delta is only ever a few fragments.
 * A dropped delta is not repaired and does not stop the picture. A later frame is released as soon
 * as it is complete; the gap is skipped. A key that is still arriving is assembled, and only a key
 * that stops arriving asks for another. The decoder, not this assembler, decides when a skipped
 * reference has made the next frame undecodable.
 */
const KEY_STALL_US = 250000, KEY_TOTAL_US = 1500000, DELTA_TOTAL_US = 500000;
const MAX_HELD_FRAMES = 16;
const MAX_TRACKED_GAPS = 64;
export interface WcReassemblyEvents {
  reserve(frameId: number, bytes: number): boolean;
  recover(retireThrough: number): void;
}
/** Owns only complete-AU assembly. A gap releases the next complete frame; it does not wait for a key. */
export class FrameReassembler {
  private readonly frames = new Map<number, Assembly>();
  private readonly gaps = new Map<number, Gap>();
  private bytes = 0;
  private floor = 0;
  private highest = 0;
  private source = 0;
  private sourceGapUs: number | null = null;
  private needsKey = true;
  /** Smoothed spacing between consecutive fragments of one frame: how fast the host is pacing. */
  private fragmentGapUs = 0;
  /** Smoothed time from a confirmed loss going on the wire to the frame's next fragment landing: a resend's round trip. */
  private repairRttUs = 0;
  /** When a stall last asked the host for a key; the key landing is a round-trip sample before any resend has. */
  private keyAskedUs: number | null = null;
  private keyAsks = 0;
  private readonly interval: number;
  constructor(private readonly epoch: number, fps: number, private readonly events: WcReassemblyEvents, repairRttUs = 0) {
    this.interval = Math.ceil(1000000 / fps); this.repairRttUs = Math.max(0, Math.round(repairRttUs));
  }
  accept(fragment: WcFragment, nowUs: number): boolean {
    if (fragment.epoch !== this.epoch || fragment.frameId <= this.floor) return false;
    this.noteGaps(fragment.frameId, fragment.key, nowUs);
    let frame = this.frames.get(fragment.frameId);
    if (!frame) {
      if (this.frames.size >= MAX_HELD_FRAMES || fragment.frameBytes > 3 * 1024 * 1024 - this.bytes ||
        !this.events.reserve(fragment.frameId, fragment.frameBytes)) { this.recover(); return false; }
      frame = { header: { ...fragment, payload: new Uint8Array() }, bytes: new Uint8Array(fragment.frameBytes),
        parts: new Map(), filled: 0, firstUs: nowUs, lastUs: nowUs,
        deadlineUs: nowUs + this.stallLimitUs(this.needsKey || fragment.key), reportedUs: null };
      this.frames.set(fragment.frameId, frame); this.bytes += fragment.frameBytes;
      if (fragment.key && this.keyAskedUs !== null) { this.sampleRepairRtt(nowUs - this.keyAskedUs); this.keyAskedUs = null; }
      const gap = this.gaps.get(fragment.frameId);
      if (gap) { // the first fragment of a frame asked for by id is the resend: one round-trip sample
        if (gap.reportedUs !== null) this.sampleRepairRtt(nowUs - gap.reportedUs);
        this.gaps.delete(fragment.frameId);
      }
    }
    if (!this.matches(frame.header, fragment)) { this.recover(); return false; }
    const duplicate = frame.parts.get(fragment.fragmentIndex);
    if (duplicate) {
      const assembledBytes = frame.bytes;
      if (duplicate.offset !== fragment.fragmentOffset || duplicate.size !== fragment.payload.length ||
        !fragment.payload.every((byte, i) => assembledBytes[duplicate.offset + i] === byte)) this.recover();
      return false;
    }
    const begin = fragment.fragmentOffset, end = begin + fragment.payload.length;
    for (const part of frame.parts.values()) if (begin < part.offset + part.size && end > part.offset) {
      this.recover(); return false;
    }
    frame.bytes.set(fragment.payload, begin); frame.parts.set(fragment.fragmentIndex, { offset: begin, size: fragment.payload.length });
    frame.filled += fragment.payload.length;
    if (frame.parts.size === fragment.fragmentCount && frame.filled !== fragment.frameBytes) { this.recover(); return false; }
    this.observeProgress(frame, nowUs);
    return true;
  }
  /** Each fragment that lands renews the deadline: a frame is abandoned when its fragments stop arriving, not for being slow. */
  private observeProgress(frame: Assembly, nowUs: number): void {
    if (frame.reportedUs !== null) {
      // The first fragment to land after the loss went on the wire is the resend (or a straggler, which
      // only reads the path shorter than it is). One sample per report: a resend of several fragments is
      // one round trip. A resend's spacing says nothing about the host's pacing.
      this.sampleRepairRtt(nowUs - frame.reportedUs); frame.reportedUs = null;
    } else if (frame.parts.size > 1) {
      const gap = Math.max(0, Math.round(nowUs - frame.lastUs));
      this.fragmentGapUs = this.fragmentGapUs ? Math.round((3 * this.fragmentGapUs + gap) / 4) : gap;
    }
    frame.lastUs = nowUs;
    const key = frame.header.key || this.needsKey;
    const total = frame.firstUs + (key ? KEY_TOTAL_US : DELTA_TOTAL_US);
    frame.deadlineUs = Math.min(total, Math.max(frame.deadlineUs, nowUs + this.stallLimitUs(key)));
  }
  /** Frames skipped over by a new highest id are gaps: nothing of them has landed yet, and that is already loss. */
  private noteGaps(frameId: number, key: boolean, nowUs: number): void {
    if (frameId <= this.highest) return;
    if (!this.needsKey && !key) { // behind a key request, or a key, every earlier frame is moot
      for (let id = Math.max(this.highest, this.floor, frameId - MAX_TRACKED_GAPS) + 1; id < frameId; ++id) {
        if (!this.frames.has(id)) this.gaps.set(id, { seenUs: nowUs, deadlineUs: nowUs, reportedUs: null });
      }
      for (const frame of this.frames.values()) {
        if (frame.header.frameId < frameId && frame.parts.size < frame.header.fragmentCount) frame.deadlineUs = nowUs;
      }
    }
    this.highest = frameId;
  }
  /**
   * Fast up, slow down: a sample can only read the path shorter than it is (a key the host sent for
   * its own reasons landing just after a stall asked for one, a resend the host's ledger sent before
   * the report arrived), so a longer sample is believed at once and a shorter one only an eighth at a time.
   */
  private sampleRepairRtt(sampleUs: number): void {
    const sample = Math.max(0, Math.round(sampleUs));
    this.repairRttUs = sample >= this.repairRttUs ? sample : Math.round((7 * this.repairRttUs + sample) / 8);
  }
  /** The measured repair round trip, to seed the next epoch's reassembler: the path does not change with the rendition. */
  getRepairRttUs(): number { return this.repairRttUs; }
  /** Integral: the deadline feeds `remainingUs` on the wire, which the host validates as an integer. */
  private stallLimitUs(key: boolean): number {
    return Math.max(key ? KEY_STALL_US : 2 * this.interval, 3 * this.fragmentGapUs);
  }
  /** Transfers the next complete frame, skipping ids a later frame has already proved missing. */
  take(): WcCompleteFrame | null {
    const complete = (frame: Assembly | undefined) =>
      !!frame && frame.parts.size === frame.header.fragmentCount && frame.filled === frame.bytes.length;
    if (this.needsKey) {
      const key = [...this.frames.values()].filter(item => item.header.key && complete(item))
        .sort((a, b) => a.header.frameId - b.header.frameId)[0];
      return key ? this.emit(key) : null;
    }
    for (const [id, frame] of [...this.frames.entries()]) {
      if (!frame.header.key && id < this.highest && !complete(frame)) {
        this.bytes -= frame.bytes.length; this.frames.delete(id);
      }
    }
    const next = this.frames.get(this.floor + 1);
    if (next?.header.key && !complete(next)) return null;
    let id = this.floor + 1;
    while (id <= this.highest && !complete(this.frames.get(id))) id++;
    const frame = this.frames.get(id);
    return frame && complete(frame) ? this.emit(frame) : null;
  }
  private emit(frame: Assembly): WcCompleteFrame {
    const header = frame.header;
    if (header.key) for (const [id, previous] of this.frames) if (id < header.frameId) {
      this.bytes -= previous.bytes.length; this.frames.delete(id);
    }
    this.frames.delete(header.frameId); this.bytes -= frame.bytes.length;
    this.floor = header.frameId; this.needsKey = false;
    for (const id of this.gaps.keys()) if (id <= this.floor) this.gaps.delete(id);
    if (this.floor >= this.source) this.sourceGapUs = null;
    return { epoch: this.epoch, frameId: header.frameId, key: header.key, captureUs: header.captureUs,
      sentUs: header.sentUs, durationUs: header.durationUs, receivedUs: frame.firstUs, bytes: frame.bytes };
  }
  observeSource(latestFrameId: number, nowUs: number): void {
    this.source = Math.max(this.source, latestFrameId);
    if (this.source > this.floor && this.sourceGapUs === null) this.sourceGapUs = nowUs;
  }
  tick(nowUs: number): void {
    // A missing delta is skipped by take(). Only a key we are already waiting on, that stops arriving, asks again.
    for (const frame of this.frames.values()) {
      if ((frame.header.key || this.needsKey) && nowUs >= frame.deadlineUs) { this.askKey(nowUs); return; }
    }
  }
  /** A stall gave up: the key that answers is a round-trip sample. */
  private askKey(nowUs: number): void { this.keyAskedUs = nowUs; ++this.keyAsks; this.recover(); }
  /** Diagnostics: how long a delta is given before it is skipped, and how many keys stalls have asked for. */
  getRepairHoldUs(): number { return this.stallLimitUs(false); }
  getKeyAsks(): number { return this.keyAsks; }
  /**
   * Nothing is asked back. A lost frame is skipped (see `tick`), not resent: the picture it carried is
   * already late, and a resend would only hold every newer frame behind it.
   */
  missing(_nowUs: number, _confirmedOnly = false): WcFeedback['missingFragments'] {
    return [];
  }
  /** True after a gap or epoch drop until the next independently decodable key is taken. */
  needsKeyframe(): boolean { return this.needsKey; }
  /** A key is landing (or being resent) right now: asking for another would only replace it. */
  assemblingKey(): boolean { for (const frame of this.frames.values()) if (frame.header.key) return true; return false; }
  getBytes(): number { return this.bytes; }
  oldestAgeUs(nowUs: number): number {
    return Math.max(0, ...[...this.frames.values()].map(frame => nowUs - frame.firstUs));
  }
  recover(): void {
    this.floor = Math.max(this.floor, this.highest, this.source); this.frames.clear(); this.gaps.clear(); this.bytes = 0;
    this.needsKey = true; this.sourceGapUs = null; this.events.recover(this.floor);
  }
  close(): void { this.frames.clear(); this.gaps.clear(); this.bytes = 0; }
  private matches(a: WcFragment, b: WcFragment): boolean {
    return a.epoch === b.epoch && a.frameId === b.frameId && a.fragmentCount === b.fragmentCount &&
      a.frameBytes === b.frameBytes && a.captureUs === b.captureUs && a.durationUs === b.durationUs && a.key === b.key &&
      b.frameBytes <= WC_LIMITS.frameBytes;
  }
}
